import React from "react";
import { Row, Col, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import FormInput from "../../FormInput";
import MotorCaseInitiatedAddFields, {
  buildMotorOdAddPayload,
} from "./MotorCaseInitiatedAddFields";

const sectionTitleStyle = { color: "#d10b0b", width: "100%" };

const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** API motor_od_details → flat formData keys for update form. */
export const mapMotorOdDetailsToFormData = (details = {}) => {
  if (!details || typeof details !== "object") return {};

  const cd = details.claim_details || {};
  const pd = details.policy_details || {};
  const rc = details.rc_details || {};
  const ins = details.insured_details || {};
  const drv = details.driver_details || {};
  const spot = details.spot_details || {};
  const police = details.police_station_visit || {};
  const hospital = details.hospital_visit || {};
  const witnesses = details.witnesses_passenger || {};

  return {
    motor_od_claim_intimation_date: parseDateValue(cd.claim_intimation_date),
    motor_od_date_of_loss: parseDateValue(cd.date_of_loss),
    motor_od_time_of_accident: cd.time_of_accident ?? null,
    motor_od_location: cd.location ?? null,
    motor_od_vehicle: cd.vehicle ?? null,
    motor_od_idv: cd.idv ?? null,
    motor_od_date_of_allocation: parseDateValue(cd.date_of_allocation),
    motor_od_trigger_for_investigation: cd.trigger_for_investigation ?? null,
    motor_od_insured_name: pd.insured_name ?? null,
    motor_od_policy_type: pd.policy_type ?? null,
    motor_od_policy_period_from: parseDateValue(pd.policy_period_from ?? pd.policy_period),
    motor_od_policy_period_to: parseDateValue(pd.policy_period_to),
    motor_od_previous_insurer: pd.previous_insurer ?? null,
    motor_od_previous_policy_no: pd.previous_policy_no ?? pd.previous_policy_no_and_expiry ?? null,
    motor_od_previous_policy_expiry_date: parseDateValue(
      pd.previous_policy_expiry_date ?? pd.previous_policy_no_and_expiry
    ),
    motor_od_registration_no: rc.registration_no ?? null,
    motor_od_owner_name: rc.owner_name ?? null,
    motor_od_rc_address: rc.address ?? null,
    motor_od_chassis_no: rc.chassis_no ?? null,
    motor_od_engine_no: rc.engine_no ?? null,
    motor_od_valid_till: parseDateValue(rc.valid_till ?? rc.valid_till_to),
    motor_od_insured_detail_name: ins.name ?? null,
    motor_od_insured_occupation: ins.occupation ?? null,
    motor_od_insured_contact: ins.contact ?? null,
    motor_od_insured_statement_and_findings: ins.statement_and_findings ?? null,
    motor_od_insured_google_timeline_call_log: ins.google_timeline_call_log ?? null,
    motor_od_driver_name: drv.name ?? null,
    motor_od_driving_license: drv.driving_license ?? null,
    motor_od_driver_injuries: drv.injuries ?? null,
    motor_od_tp_involvement: drv.tp_involvement ?? null,
    motor_od_driver_statement: drv.driver_statement ?? null,
    motor_od_driver_google_timeline_call_history: drv.google_timeline_call_history ?? null,
    motor_od_road_type: spot.road_type ?? null,
    motor_od_any_damages_part_found: spot.any_damages_part_found ?? null,
    motor_od_any_witness_found: spot.any_witness_found ?? null,
    motor_od_name_of_police: police.name_of_police ?? null,
    motor_od_details_of_fir: police.details_of_fir ?? null,
    motor_od_gist_of_fir: police.gist_of_fir ?? null,
    motor_od_name_of_hospital: hospital.name_of_hospital ?? null,
    motor_od_date_of_admission: parseDateValue(hospital.date_of_admission),
    motor_od_details_of_injured_persons: hospital.details_of_injured_persons ?? null,
    motor_od_whether_medical_paper_found: hospital.whether_medical_paper_found ?? null,
    motor_od_mlc_done_or_not: hospital.mlc_done_or_not ?? null,
    motor_od_whether_found_under_alcohol: witnesses.whether_found_under_alcohol ?? null,
    motor_od_name_of_witnesses: witnesses.name_of_witnesses ?? null,
    motor_od_police_case: witnesses.police_case ?? null,
    motor_od_toll_record: witnesses.toll_record ?? null,
  };
};

/** Full motor_od_details payload for update API. */
export const buildMotorOdUpdatePayload = (formData) => {
  const base = buildMotorOdAddPayload(formData);

  return {
    ...base,
    insured_details: {
      ...base.insured_details,
      statement_and_findings: formData.motor_od_insured_statement_and_findings ?? null,
      google_timeline_call_log: formData.motor_od_insured_google_timeline_call_log ?? null,
    },
    driver_details: {
      name: formData.motor_od_driver_name ?? null,
      driving_license: formData.motor_od_driving_license ?? null,
      injuries: formData.motor_od_driver_injuries ?? null,
      tp_involvement: formData.motor_od_tp_involvement ?? null,
      driver_statement: formData.motor_od_driver_statement ?? null,
      google_timeline_call_history: formData.motor_od_driver_google_timeline_call_history ?? null,
    },
    spot_details: {
      road_type: formData.motor_od_road_type ?? null,
      any_damages_part_found: formData.motor_od_any_damages_part_found ?? null,
      any_witness_found: formData.motor_od_any_witness_found ?? null,
    },
    police_station_visit: {
      name_of_police: formData.motor_od_name_of_police ?? null,
      details_of_fir: formData.motor_od_details_of_fir ?? null,
      gist_of_fir: formData.motor_od_gist_of_fir ?? null,
    },
    hospital_visit: {
      name_of_hospital: formData.motor_od_name_of_hospital ?? null,
      date_of_admission: formData.motor_od_date_of_admission ?? null,
      details_of_injured_persons: formData.motor_od_details_of_injured_persons ?? null,
      whether_medical_paper_found: formData.motor_od_whether_medical_paper_found ?? null,
      mlc_done_or_not: formData.motor_od_mlc_done_or_not ?? null,
    },
    witnesses_passenger: {
      whether_found_under_alcohol: formData.motor_od_whether_found_under_alcohol ?? null,
      name_of_witnesses: formData.motor_od_name_of_witnesses ?? null,
      police_case: formData.motor_od_police_case ?? null,
      toll_record: formData.motor_od_toll_record ?? null,
    },
  };
};

const MotorCaseUpdateFields = ({
  t,
  formData,
  inputHandler,
  inputDateHandler,
  inputTimeHandler,
  selectMobileHandler,
  errors,
}) => (
  <>
    <MotorCaseInitiatedAddFields
      t={t}
      formData={formData}
      inputHandler={inputHandler}
      inputDateHandler={inputDateHandler}
      inputTimeHandler={inputTimeHandler}
      selectMobileHandler={selectMobileHandler}
      errors={errors}
    />

    <Row>
      <Col lg={12}>
        <FormInput
          label={t("Statement and Findings")}
          type="textarea"
          placeholder={t("Enter Statement and Findings")}
          errors={errors}
          value={formData.motor_od_insured_statement_and_findings}
          name="motor_od_insured_statement_and_findings"
          onChange={inputHandler}
          containerClass="mb-3"
          rows={4}
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Insured Google Timeline / Call Log etc")}
          type="text"
          placeholder={t("Enter Insured Google Timeline / Call Log etc")}
          errors={errors}
          value={formData.motor_od_insured_google_timeline_call_log}
          name="motor_od_insured_google_timeline_call_log"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>
    </Row>

    <Row>
      <h3 style={sectionTitleStyle}>{t("5. Driver Details")}</h3>

      <Col lg={4}>
        <FormInput
          label={t("Name")}
          type="text"
          placeholder={t("Enter Name")}
          errors={errors}
          value={formData.motor_od_driver_name}
          name="motor_od_driver_name"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Driving License")}
          type="text"
          placeholder={t("Enter Driving License")}
          errors={errors}
          value={formData.motor_od_driving_license}
          name="motor_od_driving_license"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Injuries")}
          type="text"
          placeholder={t("Enter Injuries")}
          errors={errors}
          value={formData.motor_od_driver_injuries}
          name="motor_od_driver_injuries"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("TP Involvement")}
          type="text"
          placeholder={t("Enter TP Involvement")}
          errors={errors}
          value={formData.motor_od_tp_involvement}
          name="motor_od_tp_involvement"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={12}>
        <FormInput
          label={t("Driver Statement")}
          type="textarea"
          placeholder={t("Enter Driver Statement")}
          errors={errors}
          value={formData.motor_od_driver_statement}
          name="motor_od_driver_statement"
          onChange={inputHandler}
          containerClass="mb-3"
          rows={4}
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Driver Google Timeline / Call History etc")}
          type="text"
          placeholder={t("Enter Driver Google Timeline / Call History etc")}
          errors={errors}
          value={formData.motor_od_driver_google_timeline_call_history}
          name="motor_od_driver_google_timeline_call_history"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>
    </Row>

    <Row>
      <h3 style={sectionTitleStyle}>{t("6. Spot Details")}</h3>

      <Col lg={4}>
        <FormInput
          label={t("Road Type")}
          type="text"
          placeholder={t("Enter Road Type")}
          errors={errors}
          value={formData.motor_od_road_type}
          name="motor_od_road_type"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Any Damages Part Found")}
          type="text"
          placeholder={t("Enter Any Damages Part Found")}
          errors={errors}
          value={formData.motor_od_any_damages_part_found}
          name="motor_od_any_damages_part_found"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Any Witness Found")}
          type="text"
          placeholder={t("Enter Any Witness Found")}
          errors={errors}
          value={formData.motor_od_any_witness_found}
          name="motor_od_any_witness_found"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>
    </Row>

    <Row>
      <h3 style={sectionTitleStyle}>{t("7. Police Station Visit")}</h3>

      <Col lg={4}>
        <FormInput
          label={t("Name of Police")}
          type="text"
          placeholder={t("Enter Name of Police")}
          errors={errors}
          value={formData.motor_od_name_of_police}
          name="motor_od_name_of_police"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Details of FIR if any")}
          type="text"
          placeholder={t("Enter Details of FIR if any")}
          errors={errors}
          value={formData.motor_od_details_of_fir}
          name="motor_od_details_of_fir"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={12}>
        <FormInput
          label={t("GIST of FIR")}
          type="textarea"
          placeholder={t("Enter GIST of FIR")}
          errors={errors}
          value={formData.motor_od_gist_of_fir}
          name="motor_od_gist_of_fir"
          onChange={inputHandler}
          containerClass="mb-3"
          rows={4}
        />
      </Col>
    </Row>

    <Row>
      <h3 style={sectionTitleStyle}>{t("8. Hospital Visit")}</h3>

      <Col lg={4}>
        <FormInput
          label={t("Name of the Hospital")}
          type="text"
          placeholder={t("Enter Name of the Hospital")}
          errors={errors}
          value={formData.motor_od_name_of_hospital}
          name="motor_od_name_of_hospital"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <Form.Group className="mb-3">
          <Form.Label>{t("Date of Admission")}</Form.Label>
          <DatePicker
            selected={
              formData.motor_od_date_of_admission
                ? new Date(formData.motor_od_date_of_admission)
                : null
            }
            onChange={(value) => inputDateHandler("motor_od_date_of_admission", value)}
            dateFormat="yyyy-MM-dd"
            className="form-control"
            withPortal
            showYearDropdown
            scrollableYearDropdown
            showMonthDropdown
            scrollableMonthDropdown
            isClearable
          />
          {errors?.motor_od_date_of_admission && (
            <div className="invalid-feedback d-block">
              <p className="text-danger">{errors.motor_od_date_of_admission}</p>
            </div>
          )}
        </Form.Group>
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Details of Injured Persons")}
          type="text"
          placeholder={t("Enter Details of Injured Persons")}
          errors={errors}
          value={formData.motor_od_details_of_injured_persons}
          name="motor_od_details_of_injured_persons"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Whether Medical Paper Found or Not")}
          type="text"
          placeholder={t("Enter Whether Medical Paper Found or Not")}
          errors={errors}
          value={formData.motor_od_whether_medical_paper_found}
          name="motor_od_whether_medical_paper_found"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("MLC Done or Not")}
          type="text"
          placeholder={t("Enter MLC Done or Not")}
          errors={errors}
          value={formData.motor_od_mlc_done_or_not}
          name="motor_od_mlc_done_or_not"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>
    </Row>

    <Row>
      <h3 style={sectionTitleStyle}>{t("9. Witnesses / Passenger if any")}</h3>

      <Col lg={4}>
        <FormInput
          label={t("Whether Found Under Alcohol")}
          type="text"
          placeholder={t("Enter Whether Found Under Alcohol")}
          errors={errors}
          value={formData.motor_od_whether_found_under_alcohol}
          name="motor_od_whether_found_under_alcohol"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Name of Witnesses")}
          type="text"
          placeholder={t("Enter Name of Witnesses")}
          errors={errors}
          value={formData.motor_od_name_of_witnesses}
          name="motor_od_name_of_witnesses"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Police Case")}
          type="text"
          placeholder={t("Enter Police Case")}
          errors={errors}
          value={formData.motor_od_police_case}
          name="motor_od_police_case"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>

      <Col lg={4}>
        <FormInput
          label={t("Toll Record")}
          type="text"
          placeholder={t("Enter Toll Record")}
          errors={errors}
          value={formData.motor_od_toll_record}
          name="motor_od_toll_record"
          onChange={inputHandler}
          containerClass="mb-3"
        />
      </Col>
    </Row>
  </>
);

export default MotorCaseUpdateFields;
